import {
  addDisposer, Instance, resolveIdentifier, types,
} from 'mobx-state-tree';
import { reaction } from 'mobx';
import { workspacePath } from '../../router/paths';
import {
  CustomerProfileDtoModelType, DriverDtoModelType,
  DriverDtoModel, ProjectDtoModelType, RouteDtoModel, RouteDtoModelType, RouteTimelineDtoModel,
  RouteTimelineDtoModelType, SolutionStatusOnlyDtoModelType, UserDtoModel, VisitDtoModel, VisitDtoModelType,
  VisitLocationDtoModel, VisitLocationDtoModelType,
} from './models';
import getAppRoot from './utils/getAppRoot';
import { PanelMode } from '../../components/shared/Panel';
import {
  TopMenuView, View, isUnscheduledView,
} from '../../components/scheduling/ViewMenu';
import { PanelType } from '../../components/routes/DetailsPanel/index';
import { CSVUploadSteps } from '../../components/orders/UploadStops/types/CSVTypes';
import { RouteDriverLocation } from './DriverLocation';
import RouteEndLocationQuery from './queries/timelineView/RouteEndLocationQuery';
import RouteStartLocationQuery from './queries/timelineView/RouteStartLocationQuery';
import { IGeolocationDto } from './models/RootStore.base';
import {
  ApplicationType,
  CompanySettingsViews,
  CustomerQueryParams,
  DriverQueryParams,
  DriversPageViews,
  OrderQueryParams,
  Pages,
  SchedulingQueryParams,
  WorkspaceSettingsViews,
} from './Router/constants';
import getSelectedView from '../../helpers/getSelectedView';
import {
  viewFromParam, leftPanelOpenForView, bottomPanelOpenForView, detailsPanelForSelection,
} from '../../helpers/schedulingPanels';
import { buildDateToggleParams } from '../../helpers/buildDateToggleParams';
import { CalendarView } from '../../components/routes/RoutesCalendar/constants';
import HoveredTimelineStopEventQuery from './queries/timelineView/HoveredTimelineStopEventQuery';
import HoveredVisitQuery from './queries/visits/HoveredVisitQuery';
import { PanelsProperties } from './MapView/helpers/getPanelPropertiesAndDimensions';
import { SCHEDULING_PANEL_WIDTH } from '../../constants';
import { SettingsQueryParams } from './Router/utils/SettingsPageQueryParams';
import { withEventTrackingAsync } from './middlewares/withEventTracking';
import { VALID_EVENT_ACTIONS } from '../../analytics/eventTrackers';
import { OrderDeliveryProgressQuery } from './queries/visits/OrderDeliveryProgressQuery';
import ExperimentKey from '../../hooks/experimentKeys';
import { TREATMENT } from '../../hooks/experimentVariants';

export type StringUndefinedNull = string | undefined | null;

const Color = types.model({
  hex: types.maybe(types.string),
  rgb: types.maybe(types.array(types.number)),
});

const MarkerColor = types.model({
  visitId: types.maybe(types.string),
  color: types.maybe(Color),
});

export type RouteColor = Instance<typeof Color>;
export type RouteMarkerColor = Instance<typeof MarkerColor>;

const VisitFormModel = types.model({
  visit: types.maybe(types.safeReference(types.late(() => VisitDtoModel))),
  visible: types.boolean,
  saving: types.boolean,
});

export type IVisitForm = Instance<typeof VisitFormModel>;

const Dispatcher = types.model({
  email: types.string,
  uuid: types.string,
});

const SupportOrganization = types.model({
  name: types.maybe(types.string),
  uuid: types.maybe(types.string),
  dispatchers: types.maybe(types.array(Dispatcher)),
  userData: types.maybe(types.safeReference(types.late(() => UserDtoModel))),
});

export type organization = Instance<typeof SupportOrganization>;

const onStopChange = (self: any) => {
  // when selecting a stop, center if it is out of bounds
  addDisposer(self, reaction(() => self.selectedRouteEvent?.coordinates, (coordinates) => {
    if (!coordinates) return;
    const [lng, lat] = coordinates;

    if (!lng || !lat) {
      return;
    }

    getAppRoot(self).mapView.centerMapOnCoordinates(lng, lat, false);
  }));

  return {};
};

const onOrderChange = (self: any) => {
  // when selecting an order, center if it is out of bounds
  addDisposer(self, reaction(
    () => {
      const loc = self.selectedVisit?.activeLocation;
      return loc ? [loc.longitude, loc.latitude] as [number, number] : undefined;
    },
    (coords) => {
      if (!coords) return;
      const [visitLng, visitLat] = coords;

      if (!visitLng || !visitLat) {
        return;
      }

      getAppRoot(self).mapView.centerMapOnCoordinates(visitLng, visitLat);
    },
  ));

  return {};
};

// Only the timeline can be resized, so it's the only panel size to remember.
const onPanelSizeChange = (self: any) => {
  addDisposer(self, reaction(
    () => self.currentBottomPanelHeight,
    (bottom) => {
      if (bottom) localStorage.setItem('currentBottomPanelHeight', JSON.stringify(bottom));
    },
    { delay: 250 },
  ));
  return {};
};

const onSelectionChange = (self: any) => {
  // Clear a stale scheduling-error banner when the user navigates to a different workspace or date —
  // the failure it describes belongs to the previous selection, not the one now in view.
  addDisposer(self, reaction(
    () => [self.selectedWorkspace?.id, self.selectedDate],
    () => {
      if (self.schedulingError) self.setSchedulingError(null);
    },
  ));
  return {};
};

const UI = types.model('UI')
  .props({
    isOrdersLoadingOnInit: types.optional(types.boolean, false),
    isRoutesLoadingOnInit: types.optional(types.boolean, false),
    isWorkspaceLoading: types.optional(types.boolean, false),
    hoveredDriver: types.maybe(types.frozen<RouteDriverLocation>()),
    selectedRoutesBulk: types.map(types.boolean),
    hoveredRoute: types.maybe(types.safeReference(types.late(() => RouteDtoModel))),
    assignDriverPopoverRouteId: types.maybe(types.string),
    hoveredRouteEvent: types.maybe(types.safeReference(types.late(() => RouteTimelineDtoModel))),
    selectedDriver: types.maybe(types.safeReference(types.late(() => DriverDtoModel))),
    hoveredSuggestion: types.maybe(types.safeReference(types.late(() => VisitLocationDtoModel))),
    selectedSuggestion: types.maybe(types.safeReference(types.late(() => VisitLocationDtoModel))),
    suggestionsModal: types.optional(VisitFormModel, { visible: false, saving: false }),
    showOpenWorkspaceSwitcher: types.optional(types.boolean, false),
    hoveredVisit: types.maybe(types.safeReference(types.late(() => VisitDtoModel))),
    showMap: types.maybeNull(types.boolean),
    isOptimizing: types.maybeNull(types.boolean),
    // Persisted reason for the most recent failed optimization, so the Unscheduled panel can show
    // it after the transient failure toast is gone. Cleared when a new optimization starts or succeeds.
    schedulingError: types.maybeNull(types.string),
    isDuplicatingRoute: types.maybeNull(types.model({
      targetDate: types.maybe(types.string),
      originalRouteUuid: types.maybe(types.string),
      routeCount: types.maybe(types.number),
    })),
    isMovingRoutes: types.maybeNull(types.model({
      targetDate: types.maybe(types.string),
      routeCount: types.maybe(types.number),
      // UUID of the UpdateRouteConstraints action driving this move. The subscription handler
      // matches on this so it only claims THIS move's terminal event — UpdateRouteConstraints is
      // reused by other flows (e.g. RouteForm edits), so a boolean intent flag misattributes.
      actionUuid: types.maybe(types.string),
    })),
    isPublishing: types.optional(types.boolean, false),
    isUnpublishing: types.optional(types.boolean, false),
    showUploadDropzone: types.maybeNull(types.boolean),
    showImportDialog: types.optional(types.model({
      visible: types.maybeNull(types.boolean),
      step: types.enumeration<CSVUploadSteps>('CSVUploadSteps', [CSVUploadSteps.MAP, CSVUploadSteps.CONFIRM]),
    }), { visible: false, step: CSVUploadSteps.MAP }),
    orderForm: types.optional(VisitFormModel, { visible: false, saving: false }),
    disableConfirmReoptimizeModal: types.optional(types.boolean, false),
    disableConfirmScheduleWarningModal: types.optional(types.boolean, false),
    disableConfirmDeleteVisitModal: types.optional(types.boolean, false),
    disableConfirmDeleteCustomerModal: types.optional(types.boolean, false),
    disableConfirmChangeDeliveryMethodModal: types.optional(types.boolean, false),
    showSearchBar: types.optional(types.boolean, false),
    showToolBar: types.optional(types.boolean, true),
    showPublishedRoutes: types.optional(types.boolean, false),
    showDebugOrganizationModal: types.optional(types.boolean, false),
    showDebugOrganizationConfirmationModal: types.optional(types.boolean, false),
    showDebugOrganizationConfirmationFailureModal: types.optional(types.boolean, false),
    showVisualizeJobModal: types.optional(types.boolean, false),
    showExtendTrialModal: types.optional(types.boolean, false),
    showAppDownloadModal: types.optional(types.boolean, false),
    showSchedulePreview: types.optional(types.boolean, false),
    skipOrdersStepInScheduling: types.optional(types.boolean, false),
    supportOrganization: types.maybe(SupportOrganization),
    showAdminResetPassword: types.optional(types.boolean, false),
    previousView: types.maybe(types.enumeration<View>('View', Object.values(View || {}))),
    previousDetailsView: types.maybe(types.enumeration<PanelType>('PanelType', Object.values(PanelType))),
    unscheduledVisitsPanel: types.optional(types.enumeration<PanelMode>('PanelMode', [PanelMode.OPEN, PanelMode.EXPANDED]), PanelMode.OPEN),
    expandedBottomPanel: types.optional(types.boolean, false),
    disablePointerEvents: types.optional(types.boolean, false),
    currentBottomPanelHeight: types.maybe(types.number),
    calendarRange: types.maybe(types.frozen<[string, string]>()),
    showQuestionableandInvalidOrders: types.optional(types.boolean, false),
    showCreateCustomerProfileModal: types.optional(types.boolean, false),
    showSelectCustomerProfileModal: types.optional(types.boolean, false),
    resetPasswordEmail: types.maybe(types.string),
    resetPasswordApplicationType: types.optional(
      types.enumeration<ApplicationType>('ApplicationType', [ApplicationType.PLATFORM, ApplicationType.MOBILE]), ApplicationType.PLATFORM),
  })
  // TODO: move to mst Map Store once set up
  .volatile(() => ({
    geolocation: undefined as IGeolocationDto | undefined,
  }))
  // #region Drivers Page
  .views((self) => ({
    get shouldShowDriverInviteModal(): boolean {
      return getAppRoot(self).router.queryParams.get(DriverQueryParams.VIEW) === DriversPageViews.INVITE;
    },
  }))
  // #endregion
  // #region Order Page
  .views((self) => ({
    get currentOrdersPage(): number {
      const currentPage = Number(getAppRoot(self).router.queryParams.get(OrderQueryParams.PAGE));
      if (currentPage > 0)
        return currentPage - 1;
      return 0;
    },
  }))
  .actions((self) => ({
    setOrdersPage(page: number) {
      getAppRoot(self).router.replaceQueryParams({
        [OrderQueryParams.PAGE]: page,
      });
    },
  }))
  .views((self) => ({
    get currentCustomerProfilesPage(): number {
      const currentPage = Number(getAppRoot(self).router.queryParams.get(CustomerQueryParams.PAGE));
      if (currentPage > 0)
        return currentPage - 1;
      return 0;
    },
  }))
  .actions((self) => ({
    setCustomerProfilesPage(page: number) {
      getAppRoot(self).router.replaceQueryParams({
        [CustomerQueryParams.PAGE]: page,
      });
    },
  }))
  //#end region
  .views((self) => ({
    get areOrdersOrRoutesLoadingOnInit(): boolean {
      return self.isOrdersLoadingOnInit || self.isRoutesLoadingOnInit;
    },
    get selectedWorkspace(): ProjectDtoModelType | undefined {
      const workspaceId = getAppRoot(self).router.params?.workspaceId;

      return workspaceId ? getAppRoot(self).model.projectDtos.get(workspaceId) : undefined;
    },
    get selectedDate(): string | undefined {
      return getAppRoot(self).router.queryParams.get(SchedulingQueryParams.DATE) || undefined;
    },
    get selectedRoute(): RouteDtoModelType | undefined {
      const routeUuid = getAppRoot(self).router.queryParams.get(SchedulingQueryParams.ROUTE);

      return routeUuid ? Array.from<RouteDtoModelType>(getAppRoot(self).model.routeDtos.values()).find((r) => r.uuid === routeUuid) : undefined;
    },
    get selectedRouteEvent(): RouteTimelineDtoModelType | undefined {
      const routeEventUuid = getAppRoot(self).router.queryParams.get(SchedulingQueryParams.STOP);
      const identifiedRouteEventFromSelectedRoute = routeEventUuid ? this.selectedRoute?.timeline?.find((e) => e.eventUuid === routeEventUuid) : undefined;

      if (!identifiedRouteEventFromSelectedRoute) {
        return routeEventUuid ? Array.from<RouteTimelineDtoModelType>(getAppRoot(self).model.routeTimelineDtos.values()).find((s) => (
          s.eventUuid === routeEventUuid && (!s.isStopEvent || s.orders == null || s.orders.length > 0)
        )) : undefined;
      }
      return identifiedRouteEventFromSelectedRoute;
    },
    get isFilteringQuestionableandInvalidOrders(): boolean {
      return self.showQuestionableandInvalidOrders;
    },
    get selectedVisit(): VisitDtoModelType | undefined {
      const orderUuid = getAppRoot(self).router.queryParams.get(SchedulingQueryParams.ORDER);

      return orderUuid ? Array.from<VisitDtoModelType>(getAppRoot(self).model.visitDtos.values()).find((s) => s.uuid === orderUuid) : undefined;
    },
    get selectedCustomerProfile(): CustomerProfileDtoModelType | null {
      const customerId = getAppRoot(self).router.queryParams.get(CustomerQueryParams.CUSTOMER_PROFILE);
      const profile = customerId ? getAppRoot(self).model.customerProfileDtos?.get(customerId) : undefined;
      return profile ?? null;
    },
    get selectedDriverFromUrl(): DriverDtoModelType | undefined {
      const driverId = getAppRoot(self).router.queryParams.get(DriverQueryParams.DRIVER);
      return driverId ? getAppRoot(self).model.driverDtos?.get(driverId) ?? undefined : undefined;
    },
    get selectedMarker(): RouteTimelineDtoModelType | VisitLocationDtoModelType | undefined {
      return this.selectedRouteEvent || this.selectedVisit?.timeline;
    },
    get selectedCompanySettingsView(): CompanySettingsViews | undefined {
      const view = getAppRoot(self).router.queryParams.get(SettingsQueryParams.VIEW) as CompanySettingsViews || undefined;

      return Object.values(CompanySettingsViews).includes(view) ? view : undefined;
    },
    get selectedWorkspaceSettingsView(): WorkspaceSettingsViews | undefined {
      const view = getAppRoot(self).router.queryParams.get(SchedulingQueryParams.SETTINGS) as WorkspaceSettingsViews || undefined;

      return Object.values(WorkspaceSettingsViews).includes(view) ? view : undefined;
    },
    get selectedCalendarView(): CalendarView | undefined {
      const view = getAppRoot(self).router.queryParams.get(SchedulingQueryParams.CALENDAR) as CalendarView || undefined;

      return Object.values(CalendarView).includes(view) ? view : undefined;
    },
  }))
  .views((self) => ({
    get defaultWorkspaceId(): string | undefined {
      return (getAppRoot(self).account.user?.defaultWorkspaceId || self.selectedWorkspace?.id)?.toString();
    },
    // The workspace to surface in the nav. Falls back to the user's default
    // (i.e. previously visited) workspace so the switcher always reflects a
    // workspace, even on non-workspace routes like /settings or /insights.
    get displayedWorkspace(): ProjectDtoModelType | undefined {
      if (self.selectedWorkspace) {
        return self.selectedWorkspace;
      }

      return this.defaultWorkspaceId
        ? getAppRoot(self).model.projectDtos.get(this.defaultWorkspaceId)
        : undefined;
    },
    get currentView(): View | undefined {
      return viewFromParam(getAppRoot(self).router.queryParams.get(SchedulingQueryParams.VIEW));
    },
    // Whether the unscheduled order tray is available on the Routes page at all. Synced workspaces
    // have never supported it, and the `exp_orders_first_landing` treatment arm drops it too: those
    // organizations manage unscheduled orders on the Orders page, so their Routes page is
    // routes-only. Every view-resolution path (setCurrentView, selectDate, the tab-matches-date
    // reaction, viewValidator) funnels through this, so no entrypoint can strand them on a tray the
    // view selector offers no way back out of.
    get canViewUnscheduledViews(): boolean {
      if (self.selectedWorkspace?.isSynced) return false;
      return getAppRoot(self).account.experimentVariant(ExperimentKey.OrdersFirstLanding) !== TREATMENT;
    },
    // True when the user is on the Routes page viewing a Not Scheduled
    // (unscheduled) tab — i.e. already where a "view orders" link would land.
    get isViewingNotScheduledTab(): boolean {
      const onRoutesPage = getAppRoot(self).router.location.pathname.includes(`/${Pages.SCHEDULING}`);
      return onRoutesPage && !!isUnscheduledView(self.currentView ?? null);
    },
    // Check whether the project is unscheduling a visit in any route
    get isUnschedulingStops(): boolean {
      return !!Array.from<SolutionStatusOnlyDtoModelType>(getAppRoot(self).model.solutionStatusOnlyDtos.values()).find((s) => s.actionType === 'unassign');
    },
    get showUnscheduledVisits(): boolean {
      // TODO: Re-assess in a coming story how and when we see unscheduled stops
      // return self.currentView === View.UNSCHEDULED_LIST;
      return true;
    },
    get getBulkRoutesArray(): string[] {
      return Array.from(self.selectedRoutesBulk.keys());
    },
    get isLeftPanelOpen(): boolean {
      return leftPanelOpenForView(this.currentView);
    },
    get isRightPanelOpen(): boolean {
      return !!self.selectedVisit || !!self.selectedRoute || !!self.selectedRouteEvent;
    },
    get isBottomPanelOpen(): boolean {
      return bottomPanelOpenForView(this.currentView);
    },
    get currentDetailsView(): PanelType | undefined {
      return detailsPanelForSelection({
        hasSelectedVisit: !!self.selectedVisit,
        hasSelectedRoute: !!self.selectedRoute,
        hasSelectedRouteEvent: !!self.selectedRouteEvent,
      });
    },
    // How much of the map each panel covers, for the fits that center on the part
    // of it the user can see. The side drawers are fixed at the panel width — only
    // the timeline is resizable — so they report that, rather than a width
    // remembered from when they could be dragged.
    get panelProperties(): PanelsProperties {
      return {
        isRightPanelOpen: this.isRightPanelOpen,
        isLeftPanelOpen: this.isLeftPanelOpen,
        isBottomPanelOpen: this.isBottomPanelOpen,
        currentLeftPanelWidth: SCHEDULING_PANEL_WIDTH,
        currentRightPanelWidth: SCHEDULING_PANEL_WIDTH,
        currentBottomPanelHeight: self.currentBottomPanelHeight,
      };
    },
  }))
  .actions(onStopChange)
  .actions(onOrderChange)
  .actions(onSelectionChange)
  .actions((self) => ({
    resetRouteMarkerSelection() {
      getAppRoot(self).router.replaceQueryParams({
        [SchedulingQueryParams.STOP]: null,
        [SchedulingQueryParams.ROUTE]: null,
        [SchedulingQueryParams.ORDER]: null,
      });
    },
    setIsOrdersLoadingOnInit(loading: boolean) {
      self.isOrdersLoadingOnInit = loading;
    },
    setIsRoutesLoadingOnInit(loading: boolean) {
      self.isRoutesLoadingOnInit = loading;
    },
    setCurrentBottomPanelHeight(height: number | undefined) {
      self.currentBottomPanelHeight = height;
    },
    resetSelectedItems() {
      getAppRoot(self).mapView.setSelectedItems(undefined);
      getAppRoot(self).mapView.setLassoMode(null);
      getAppRoot(self).mapView.exitDrawMode();
    },
    selectWorkspace(identifier?: string | null) {
      self.isWorkspaceLoading = true;

      // Remove the selected date before navigating to a new project when selecting from the UI in order to fall back to default routing logic
      localStorage.removeItem('selectedWorkspaceDate');

      if (identifier == null) {
        localStorage.removeItem('selectedWorkspaceId');
        getAppRoot(self).router.push({ pathname: '/' });
        return;
      }

      const view = getAppRoot(self).router.isWorkspaceMain ? getAppRoot(self).router.currentAppView : '';

      getAppRoot(self).router.push({ pathname: `${workspacePath}/${identifier}/${view}` });

      self.isWorkspaceLoading = false;
    },
    hoverDriver(hoveredDriver: RouteDriverLocation) {
      self.hoveredDriver = hoveredDriver;
    },
    hoverRoute(identifier: StringUndefinedNull) {
      self.hoveredRoute = identifier ? resolveIdentifier(RouteDtoModel, self, identifier) : undefined;
    },
    setAssignDriverPopoverRouteId(routeId: string | undefined) {
      self.assignDriverPopoverRouteId = routeId;
    },
    insertSelectedRoutesBulk(routeId: string) {
      const numOfSelectedOrders = getAppRoot(self).mapView.ordersWithinLassoCount;
      if (numOfSelectedOrders > 0) {
        getAppRoot(self).mapView.setSelectedItems(undefined);
      }
      if (getAppRoot(self).mapView.isDrawMode) {
        getAppRoot(self).ui.selectRoute(undefined);
        getAppRoot(self).ui.resetSelectedItems();
      }

      self.selectedRoutesBulk.set(routeId, true);
    },
    deleteSelectedRoutes() {
      const routeIds = Array.from(self.selectedRoutesBulk.keys());
      if (routeIds.length > 0) {
        routeIds.forEach((routeId) => {
          self.selectedRoutesBulk.delete(routeId || '');
        });
      }
    },
    deleteSelectedRoutesBulk(routeUuid?: string) {
      if (routeUuid !== undefined) { self.selectedRoutesBulk.delete(routeUuid || ''); }
    },
    clearSelectedRoutesBulk() {
      self.selectedRoutesBulk.clear();
    },
    setResetPasswordEmail(email: string) {
      self.resetPasswordEmail = email;
    },
    setResetPasswordApplicationType(applicationType: ApplicationType) {
      self.resetPasswordApplicationType = applicationType;
    },
    setCalendarRange(range: [string, string]) {
      self.calendarRange = range;
    },
    setShowQuestionableandInvalidOrders(filterVisitsErrorsandWarnings: boolean = false) {
      self.showQuestionableandInvalidOrders = filterVisitsErrorsandWarnings;
    },
    selectRouteEvent(identifier: StringUndefinedNull, closeOrderPanel: boolean = true) {
      this.setShowSearchBar(false);

      const identifiedStop = identifier ? resolveIdentifier(RouteTimelineDtoModel, self, identifier) : undefined;

      const isAlreadySelected = identifiedStop?.id === self.selectedRouteEvent?.id && closeOrderPanel;

      if (isAlreadySelected) {
        if (identifiedStop?.coordinates) {
          const [lng, lat] = identifiedStop.coordinates;
          getAppRoot(self).mapView.centerMapOnCoordinates(lng, lat, true);
        }
      }

      const updatedStopUuid = !isAlreadySelected ? identifiedStop?.eventUuid : undefined;
      const route = identifiedStop?.route;

      // Skip the route pan — the onStopChange reaction handles centering on
      // the stop coordinates instead.  Only set the flag when the route is
      // actually changing; otherwise the useEffect won't fire and the stale
      // flag would incorrectly suppress the next legitimate route pan.
      const isRouteChanging = route && route.id !== self.selectedRoute?.id;
      if (isRouteChanging) {
        getAppRoot(self).mapView.setSkipNextRoutePan(true);
      }

      getAppRoot(self).router.replaceQueryParams({
        [SchedulingQueryParams.STOP]: updatedStopUuid,
        ...(route ? { [SchedulingQueryParams.ROUTE]: route.uuid } : []),
        ...(closeOrderPanel ? { [SchedulingQueryParams.ORDER]: null } : []),
      });
    },
    hoverRouteEvent(identifier: StringUndefinedNull, query = false, hoverOnMap: boolean = true) {
      if (getAppRoot(self).timeline.draggingVisit) return;
      const routeEvent = identifier ? resolveIdentifier(RouteTimelineDtoModel, self, identifier) : undefined;

      const hoverOnMapAndPopUpEnabled = hoverOnMap && getAppRoot(self).mapView.mapPopupEnabled;
      const shouldHoverStopDetails = hoverOnMapAndPopUpEnabled || !hoverOnMap;

      if (routeEvent?.eventUuid && routeEvent?.eventUuid !== null && query && shouldHoverStopDetails) {
        this.loadHoverStopDetails(routeEvent?.eventUuid);
      }
      self.hoveredRouteEvent = routeEvent;
    },
    hoverRouteStartEnd(identifier: StringUndefinedNull, type: 'start' | 'end') {
      if (getAppRoot(self).timeline.draggingVisit) return;
      const routeEvent = identifier ? resolveIdentifier(RouteTimelineDtoModel, self, identifier) : undefined;
      if (routeEvent != null && routeEvent?.route?.id !== null) {
        this.loadHoverRouteConstraint(routeEvent?.route?.id, type);
      }
      self.hoveredRouteEvent = routeEvent;
    },
    selectDriver(identifier: StringUndefinedNull) {
      const identifiedDriver = identifier ? getAppRoot(self).model.driverDtos?.get(identifier) : undefined;
      const isAlreadySelected = identifiedDriver?.id === self.selectedDriverFromUrl?.id;

      getAppRoot(self).router.replaceQueryParams({
        [DriverQueryParams.DRIVER]: isAlreadySelected ? null : identifier,
      });
    },
    selectCustomerProfile(identifier: StringUndefinedNull) {
      const router = getAppRoot(self).router;
      const isAlreadySelected = identifier === router.queryParams.get(CustomerQueryParams.CUSTOMER_PROFILE)
        && !router.queryParams.has(OrderQueryParams.ORDER);

      router.replaceQueryParams({
        [CustomerQueryParams.CUSTOMER_PROFILE]: isAlreadySelected ? null : identifier,
        [OrderQueryParams.ORDER]: null,
      });
    },
    selectVisit(identifier: StringUndefinedNull) {
      this.setShowSearchBar(false);
      const identifiedVisit = identifier ? resolveIdentifier(VisitDtoModel, self, identifier) : undefined;

      const stopEvent = identifiedVisit?.timeline;
      const routeEvent = identifiedVisit?.route;

      const isAlreadySelected = identifiedVisit?.id === self.selectedVisit?.id;

      if (isAlreadySelected) {
        const [visitLng, visitLat] = [identifiedVisit?.activeLocation.longitude, identifiedVisit?.activeLocation.latitude];

        if (!!visitLng && !!visitLat) {
          getAppRoot(self).mapView.centerMapOnCoordinates(visitLng, visitLat, true);
        }
      }

      const updatedOrderUuid = !isAlreadySelected ? identifiedVisit?.uuid : undefined;

      getAppRoot(self).router.replaceQueryParams({
        [SchedulingQueryParams.ORDER]: updatedOrderUuid,
        ...((self.selectedRouteEvent && stopEvent && stopEvent?.id !== self.selectedRouteEvent?.id) || !identifiedVisit?.isScheduled ? { [SchedulingQueryParams.STOP]: null } : []),
        ...((self.selectedRoute && routeEvent && routeEvent?.id !== self.selectedRoute?.id) || !identifiedVisit?.isScheduled ? { [SchedulingQueryParams.ROUTE]: null } : []),
      });
      getAppRoot(self).mapView.exitDrawMode();
    },
    selectSuggestion(identifier: StringUndefinedNull, locationidentifier: StringUndefinedNull) {
      const visit = identifier ? resolveIdentifier(VisitDtoModel, self, identifier) : undefined;
      self.selectedSuggestion = (locationidentifier !== null && locationidentifier !== undefined) ? visit?.locations.filter((l) => l.id === locationidentifier)[0] : undefined;
    },
    setShowPublishedRoutes(show: boolean) {
      if (getAppRoot(self).account.isRoutician) {
        self.showPublishedRoutes = show;
      }
    },
    setShowSuggestionsModal(orderForm: IVisitForm) {
      self.suggestionsModal = orderForm;
    },
    openWorkspaceSwitcher(open: boolean) {
      self.showOpenWorkspaceSwitcher = open;
    },
    setShowSettingsModal(view?: WorkspaceSettingsViews) {
      if (view) {
        this.openWorkspaceSwitcher(false);
      }
      getAppRoot(self).router.replaceQueryParams({ [SchedulingQueryParams.SETTINGS]: view });
    },
    setShowDebugOrganizationModal(value: boolean) {
      self.showDebugOrganizationModal = value;
    },
    setShowDebugOrganizationConfirmationModal(value: boolean) {
      self.showDebugOrganizationConfirmationModal = value;
    },
    setShowDebugOrganizationConfirmationFailureModal(value: boolean) {
      self.showDebugOrganizationConfirmationFailureModal = value;
    },
    setSupportOrganization(supportOrganization: organization) {
      self.supportOrganization = supportOrganization;
    },
    setShowResetPasswordAsAdmin(value: boolean) {
      self.showAdminResetPassword = value;
    },
    setShowVisualizeJobModal(value: boolean) {
      self.showVisualizeJobModal = value;
    },
    setShowExtendTrialModal(show: boolean) {
      if (getAppRoot(self).account.isRoutician) {
        self.showExtendTrialModal = show;
      }
    },
    setShowAppDownloadModal(value: boolean) {
      self.showAppDownloadModal = value;
    },
    setShowCreateCustomerProfileModal(value: boolean) {
      self.showCreateCustomerProfileModal = value;
    },
    setShowSelectCustomerProfileModal(value: boolean) {
      self.showSelectCustomerProfileModal = value;
    },
    setShowSchedulePreview(value: boolean) {
      self.showSchedulePreview = value;
      if (!value) {
        self.skipOrdersStepInScheduling = false;
      }
    },
    setSkipOrdersStepInScheduling(value: boolean) {
      self.skipOrdersStepInScheduling = value;
    },
    loadHoverStopDetails(identifier: StringUndefinedNull) {
      if (getAppRoot(self).timeline.draggingVisit) return;
      const projectId = self.selectedWorkspace?.id;

      if (projectId != null && identifier != null) {
        getAppRoot(self).model.queryRouteTimelineEvent({
          projectId,
          eventUuid: identifier,
        }, HoveredTimelineStopEventQuery, { fetchPolicy: 'cache-and-network' }).currentPromise();
      }
    },
    loadHoverOrderDetails(identifier: StringUndefinedNull) {
      if (getAppRoot(self).timeline.draggingVisit) return;
      const projectId = self.selectedWorkspace?.id;

      if (projectId != null && identifier != null) {
        getAppRoot(self).model.queryVisitById({
          projectId,
          visitId: identifier,
        }, HoveredVisitQuery, { fetchPolicy: 'cache-and-network' }).currentPromise();
      }
    },
    loadHoverRouteConstraint(routeId: StringUndefinedNull, type: 'start' | 'end') {
      if (getAppRoot(self).timeline.draggingVisit) return;
      const projectId = self.selectedWorkspace?.id;

      if (projectId != null && routeId != null) {
        getAppRoot(self).model.queryRouteById({
          projectId,
          routeId,
        }, type === 'start' ? RouteStartLocationQuery : RouteEndLocationQuery, { fetchPolicy: 'no-cache' }).currentPromise();
      }
    },

    fetchOrderDeliveryProgress(projectId: number, routeUuids: string[]) {
      return getAppRoot(self).model.queryOrderDeliveryProgress({ projectId, routeUuids }, OrderDeliveryProgressQuery, { fetchPolicy: 'cache-and-network' })
        .currentPromise();
    },

    hoverSuggestion(visitIdentifier: StringUndefinedNull, locationidentifier: StringUndefinedNull) {
      const visit = visitIdentifier ? resolveIdentifier(VisitDtoModel, self, visitIdentifier) : undefined;
      self.hoveredSuggestion = (locationidentifier !== null && locationidentifier !== undefined) ? visit?.locations.filter((l) => l.id === locationidentifier)[0] : undefined;
    },
    hoverVisit(identifier: StringUndefinedNull, query = false) {
      if (getAppRoot(self).timeline.draggingVisit) return;

      if (query) {
        this.loadHoverOrderDetails(identifier);
      }

      self.hoveredVisit = identifier ? resolveIdentifier(VisitDtoModel, self, identifier) : undefined;
    },
    refreshMultiSelectedUnscheduledVisits() {
      // re-filter multi-selected unscheduled visits to exclude recently scheduled
      const { selectedItems } = getAppRoot(self).mapView;
      getAppRoot(self).mapView.setSelectedItems({
        stops: selectedItems?.stops.map((v) => v.id),
        unscheduledOrders: [...(selectedItems?.unscheduledOrders.map((v) => v.id) || [])],
      });
    },
    setShowMap(visible: boolean) {
      self.showMap = visible;
    },
    selectDate(date: string | undefined, view?: TopMenuView) {
      // buildDateToggleParams clears the view on a plain date toggle so the
      // tab-matches-date rule re-decides for the new date (restoring the
      // stored sub-view preference); an explicit `view` is honored.
      getAppRoot(self).router.replaceQueryParams(
        buildDateToggleParams(date, view, self.canViewUnscheduledViews),
      );
      getAppRoot(self).mapView.exitDrawMode();
    },
    setCurrentView(view: View | TopMenuView) {
      self.previousView = self.currentView;

      const newView = getSelectedView(view, { allowUnscheduledViews: self.canViewUnscheduledViews });

      getAppRoot(self).router.replaceQueryParams({ [SchedulingQueryParams.VIEW]: newView });
    },
    // TODO: Replace selectRoute with navigateToRouteResource
    // Github issue: https://github.com/routific/planning-web/issues/3183
    selectRoute(identifier: StringUndefinedNull, deselectStop: boolean = true, deselectOrder: boolean = true, keepRouteSelected?: boolean) {
      if (identifier) {
        const resolved = resolveIdentifier(RouteDtoModel, self, identifier);
        if (!resolved) {
          const appRoot = getAppRoot(self);
          appRoot.model.fetchRouteById(identifier).then(() => {
            appRoot.ui.selectRoute(identifier, deselectStop, deselectOrder, keepRouteSelected);
          });
          return;
        }
      }

      const identifiedRoute = identifier ? resolveIdentifier(RouteDtoModel, self, identifier) : undefined;
      const isAlreadySelected = identifiedRoute?.id === self.selectedRoute?.id && (!deselectStop && !self.selectedRouteEvent);

      let updatedRouteUuid;

      if (keepRouteSelected) {
        updatedRouteUuid = identifiedRoute?.uuid;
      } else {
        updatedRouteUuid = !isAlreadySelected ? identifiedRoute?.uuid : undefined;
      }

      getAppRoot(self).router.replaceQueryParams({
        [SchedulingQueryParams.ROUTE]: updatedRouteUuid,
        ...(deselectStop ? { [SchedulingQueryParams.STOP]: null } : []),
        ...(deselectOrder ? { [SchedulingQueryParams.ORDER]: null } : []),
      });
    },
    setUnscheduledVisitsPanel(mode: PanelMode) {
      if (mode === PanelMode.EXPANDED) {
        if (self.showSearchBar) {
          this.setShowSearchBar(false);
        }
        if (self.selectedRoute) {
          getAppRoot(self).ui.selectRoute(undefined);
        }
      }

      self.unscheduledVisitsPanel = mode;
    },
    clearSelection() {
      getAppRoot(self).router.replaceQueryParams({
        [SchedulingQueryParams.ROUTE]: null,
        [SchedulingQueryParams.STOP]: null,
        [SchedulingQueryParams.ORDER]: null,
      });
    },
    setShowSearchBar(show: boolean) {
      if (show) {
        this.clearSelection();
      }

      self.showSearchBar = show;
    },
    // todo: remove these calls and have isOptimizing be inferred from
    // a pending solution of actionType 'optimize_project'.
    setIsOptimizing(optimizing: boolean | null) {
      self.isOptimizing = optimizing;
    },
    setSchedulingError(error: string | null) {
      self.schedulingError = error;
    },
    setIsDuplicatingRoute(duplicating: { targetDate?: string; originalRouteUuid?: string; routeCount?: number } | null) {
      self.isDuplicatingRoute = duplicating;
    },
    setIsMovingRoutes(moving: { targetDate: string | undefined; routeCount: number | undefined; actionUuid: string | undefined } | null) {
      self.isMovingRoutes = moving;
    },
    setIsPublishing(publishing: boolean) {
      self.isPublishing = publishing;
    },
    setIsUnpublishing(unpublishing: boolean) {
      self.isUnpublishing = unpublishing;
    },
    setShowUploadDropzone(showUploadDropzone: boolean | null) {
      self.showUploadDropzone = showUploadDropzone;
    },
    setShowImportDialog: withEventTrackingAsync(self, VALID_EVENT_ACTIONS.UI_SHOW_IMPORT_DIALOG,
      function (visible: boolean | null, step?: CSVUploadSteps) {
        if (step) {
          self.showImportDialog = { visible, step };
        } else {
          self.showImportDialog = { visible, step: CSVUploadSteps.MAP };
        }
      }),
    setShowCalendarView(view?: CalendarView) {
      this.setDisablePointerEvents(!!view);

      getAppRoot(self).router.replaceQueryParams({ [SchedulingQueryParams.CALENDAR]: view });
    },
    setOrderForm(orderForm: IVisitForm) {
      self.orderForm = orderForm;
    },
    closeOrderForm() {
      self.orderForm = { visible: false, saving: false, visit: undefined };
    },
    setDisableConfirmReoptimizeModal(state: boolean) {
      self.disableConfirmReoptimizeModal = state;
      localStorage.setItem('disableConfirmReoptimizeLassoModal', JSON.stringify(state));
    },
    setDisableConfirmScheduleWarningModal(state: boolean) {
      self.disableConfirmScheduleWarningModal = state;
      localStorage.setItem('disableConfirmScheduleWarningModal', JSON.stringify(state));
    },
    setDisableConfirmDeleteVisitModal(state: boolean) {
      self.disableConfirmDeleteVisitModal = state;
      localStorage.setItem('disableConfirmDeleteVisitModal', JSON.stringify(state));
    },
    setDisableConfirmDeleteCustomerModal(state: boolean) {
      self.disableConfirmDeleteCustomerModal = state;
      localStorage.setItem('disableConfirmDeleteCustomerModal', JSON.stringify(state));
    },
    setDisableConfirmChangeDeliveryMethodModal(state: boolean) {
      self.disableConfirmChangeDeliveryMethodModal = state;
      localStorage.setItem('disableConfirmChangeDeliveryMethodModal', JSON.stringify(state));
    },
    setDisablePublishSummaryModal(state: boolean) {
      self.disablePublishSummaryModal = state;
      localStorage.setItem('disablePublishSummaryModal', JSON.stringify(state));
    },
    selectVisitAndRoute(id: StringUndefinedNull, routeId: StringUndefinedNull) {
      getAppRoot(self).ui.selectVisit(id);
      getAppRoot(self).ui.selectRoute(routeId, false);
    },
    // TODO: Replace selectOrderAndStopByUuid with navigateToRouteResource
    // Github issue: https://github.com/routific/planning-web/issues/3183
    selectOrderAndStopByUuid(orderUuid: StringUndefinedNull, stopUuid: StringUndefinedNull) {
      if (!orderUuid && !stopUuid) {
        getAppRoot(self).router.replaceQueryParams({
          [SchedulingQueryParams.ORDER]: null,
          [SchedulingQueryParams.STOP]: null,
        });
        return;
      }

      const queryParams = {
        [SchedulingQueryParams.ORDER]: orderUuid,
        [SchedulingQueryParams.STOP]: stopUuid,
      };

      getAppRoot(self).router.replaceQueryParams(queryParams);
    },
    setGeolocation(geoLocation: IGeolocationDto | undefined) {
      self.geolocation = geoLocation;
    },
    setExpandBottomPanel(expand: boolean) {
      this.setDisablePointerEvents(true);
      self.expandedBottomPanel = expand;
    },
    setDisablePointerEvents(disable: boolean) {
      self.disablePointerEvents = disable;
      if (disable) {
        setTimeout(
          () => this.setDisablePointerEvents(false),
          500,
        );
      }
    },
    setWorkspaceAsDeleted(workspaceId: string) {
      const workspace = getAppRoot(self).model.projectDtos.get(workspaceId);
      if (workspace) {
        getAppRoot(self).model.projectDtos.set(workspaceId, { ...workspace, deleted: true });
      }
    },
  }))
  .actions(onPanelSizeChange);

export default UI;

export interface IUIStore extends Instance<typeof UI> { }
